/** Rigid right-handed poses, Hamilton quaternions [x,y,z,w], metres. */
export type Vec3Tuple = readonly [number, number, number];
export type QuatTuple = readonly [number, number, number, number];
export interface RigidPose { readonly t: Vec3Tuple; readonly q: QuatTuple; }

export const IDENTITY_POSE: RigidPose = { t: [0, 0, 0], q: [0, 0, 0, 1] };

export function quatMultiply(a: QuatTuple, b: QuatTuple): QuatTuple {
    const [ax, ay, az, aw] = a;
    const [bx, by, bz, bw] = b;
    return [
        aw * bx + ax * bw + ay * bz - az * by,
        aw * by - ax * bz + ay * bw + az * bx,
        aw * bz + ax * by - ay * bx + az * bw,
        aw * bw - ax * bx - ay * by - az * bz,
    ];
}

export function quatConjugate(q: QuatTuple): QuatTuple {
    return [-q[0], -q[1], -q[2], q[3]];
}

export function quatNormalize(q: readonly number[]): QuatTuple {
    const len = Math.hypot(q[0], q[1], q[2], q[3]);
    if (!(len > 1e-12)) return [0, 0, 0, 1];
    return [q[0] / len, q[1] / len, q[2] / len, q[3] / len];
}

export function quatRotate(q: QuatTuple, v: Vec3Tuple): Vec3Tuple {
    const [qx, qy, qz, qw] = q;
    // t = 2 · (q.xyz × v);  v' = v + w·t + q.xyz × t
    const tx = 2 * (qy * v[2] - qz * v[1]);
    const ty = 2 * (qz * v[0] - qx * v[2]);
    const tz = 2 * (qx * v[1] - qy * v[0]);
    return [
        v[0] + qw * tx + (qy * tz - qz * ty),
        v[1] + qw * ty + (qz * tx - qx * tz),
        v[2] + qw * tz + (qx * ty - qy * tx),
    ];
}

/** Angle (rad, 0…π) of the rotation taking `a` to `b`. */
export function quatAngle(a: QuatTuple, b: QuatTuple): number {
    const d = Math.abs(a[0] * b[0] + a[1] * b[1] + a[2] * b[2] + a[3] * b[3]);
    return 2 * Math.acos(Math.min(1, d));
}

/** Shortest-arc spherical interpolation. */
export function quatSlerp(a: QuatTuple, b: QuatTuple, s: number): QuatTuple {
    let [bx, by, bz, bw] = b;
    let cos = a[0] * bx + a[1] * by + a[2] * bz + a[3] * bw;
    if (cos < 0) {
        cos = -cos; bx = -bx; by = -by; bz = -bz; bw = -bw;
    }
    let ka: number;
    let kb: number;
    if (cos > 0.9995) {
        ka = 1 - s;
        kb = s;
    } else {
        const theta = Math.acos(cos);
        const sin = Math.sin(theta);
        ka = Math.sin((1 - s) * theta) / sin;
        kb = Math.sin(s * theta) / sin;
    }
    return quatNormalize([
        ka * a[0] + kb * bx,
        ka * a[1] + kb * by,
        ka * a[2] + kb * bz,
        ka * a[3] + kb * bw,
    ]);
}

/**
 * The twist of `q` about +Y (swing–twist decomposition): the pure yaw.
 * Identity when `q` is a half-turn about a horizontal axis (no defined yaw).
 */
export function quatYawOnly(q: QuatTuple): QuatTuple {
    const len = Math.hypot(q[1], q[3]);
    if (len < 1e-9) return [0, 0, 0, 1];
    return [0, q[1] / len, 0, q[3] / len];
}

/** Yaw angle (rad) of `q` about +Y, in (−π, π]. */
export function quatYaw(q: QuatTuple): number {
    const y = quatYawOnly(q);
    return 2 * Math.atan2(y[1], y[3]);
}

/** `T_a←c = T_a←b · T_b←c`. */
export function poseMultiply(ab: RigidPose, bc: RigidPose): RigidPose {
    const rt = quatRotate(ab.q, bc.t);
    return {
        t: [ab.t[0] + rt[0], ab.t[1] + rt[1], ab.t[2] + rt[2]],
        q: quatNormalize(quatMultiply(ab.q, bc.q)),
    };
}

/** `T_b←a` from `T_a←b`. */
export function poseInvert(ab: RigidPose): RigidPose {
    const qi = quatConjugate(ab.q);
    const t = quatRotate(qi, ab.t);
    return { t: [-t[0], -t[1], -t[2]], q: qi };
}

/** Apply a pose to a point. */
export function poseTransformPoint(p: RigidPose, v: Vec3Tuple): Vec3Tuple {
    const r = quatRotate(p.q, v);
    return [r[0] + p.t[0], r[1] + p.t[1], r[2] + p.t[2]];
}

/** Angle (rad, 0…π) between `q`'s rotated +Y and world +Y. */
export function quatTilt(q: QuatTuple): number {
    // (q · +Y).y = 1 − 2(x² + z²)
    return Math.acos(Math.max(-1, Math.min(1, 1 - 2 * (q[0] * q[0] + q[2] * q[2]))));
}

/**
 * Reduce a scene pose to yaw-only **about the marker**: `pivot` is the
 * marker's origin in scene coordinates (`P.t`). The marker point stays where
 * it was measured and the scene turns upright around it, as Unity's
 * `SmoothAlignedTransform` rotates the marker transform in place. Pivoting
 * about the scene origin instead would swing the content, and amplify tilt
 * noise, by the marker's distance from it.
 */
export function poseGravityAlignedAbout(p: RigidPose, pivot: Vec3Tuple): RigidPose {
    const m = poseTransformPoint(p, pivot);
    const q = quatYawOnly(p.q);
    const r = quatRotate(q, pivot);
    return { t: [m[0] - r[0], m[1] - r[1], m[2] - r[2]], q };
}

/**
 * The auto snap for a single, stateless pose (a confirmed manual alignment):
 * yaw-only about the marker `pivot` when the tilt is within `acos(minUpDot)`,
 * else as given. The per-localizer, sticky version lives in
 * single-source stabilizer.
 */
export function poseAutoSnapped(
    p: RigidPose,
    pivot: Vec3Tuple = [0, 0, 0],
    minUpDot: number = 0.85,
): RigidPose {
    return quatTilt(p.q) <= Math.acos(minUpDot) ? poseGravityAlignedAbout(p, pivot) : p;
}

/** Keep translation and yaw about +Y; drop pitch and roll. */
export function poseGravityAligned(p: RigidPose): RigidPose {
    return { t: p.t, q: quatYawOnly(p.q) };
}

/**
 * Equation (1): the scene pose one localizer sighting implies.
 * `T_world←scene = T_world←L · (T_scene←L)⁻¹`.
 */
export function sceneFromObservation(worldFromL: RigidPose, sceneFromL: RigidPose): RigidPose {
    return poseMultiply(worldFromL, poseInvert(sceneFromL));
}

export function dist(a: Vec3Tuple, b: Vec3Tuple): number {
    return Math.hypot(a[0] - b[0], a[1] - b[1], a[2] - b[2]);
}

export function lerp3(a: Vec3Tuple, b: Vec3Tuple, s: number): Vec3Tuple {
    return [a[0] + (b[0] - a[0]) * s, a[1] + (b[1] - a[1]) * s, a[2] + (b[2] - a[2]) * s];
}

